<template id="messages-overview">
        <div class="flex gap-7 messages-overview-container">

            <!-- Your messages section -->
            <div
                class="card-box col-md-3 flex flex-col p-0 overflow-y-auto conversation-list fullscreen-on-small"
                :class="{'hide-on-small' : selectedClient || showSelectClient }"
            >
                <infinite-loading
                    :is-loading="messagesLoading"
                    :is-dropdown="false"
                    :has-finished-loading="!messagesAllLoaded"
                    @load-more="getMessages(true)"
                >
                    <div class="px-7 py-6 flex align-center sticky top-0 bg-white custom-divider">
                        <i class="fa-regular fa-chevron-left fa-lg m-r-10 hide-on-regular" @click="previousPage()"></i>
                        <p class="header-title m-b-0 flex-1 n-color-text"> {{ trans('messages.your_messages') }} </p>
                        <ep-button variant="secondary" @click="showSelectClient = true; selectedClient = null">
                            <i class="fa-regular fa-lg fa-pen-to-square"></i>
                        </ep-button>
                    </div>

                    <conversation-item
                        v-for="conversation in allConversations"
                        :key="conversation.client.id"
                        :conversation="conversation"
                        :selected-client="selectedClient"
                        @select-client="setSelectedClient"
                    ></conversation-item>
                </infinite-loading>

                <div v-if="allConversations.length === 0 && !messagesLoading" class="flex flex-col h-full align-center justify-center">
                    <i class="fa-duotone fa-messages text-primary text-5xl"></i>
                    <p class="text-muted text-md font-bold text-center"> {{ trans('messages.no_messages') }} </p>
                </div>
            </div>

            <!-- Conversation content section -->
            <div
                class="card-box col-md-9 flex flex-col p-0 fullscreen-on-small"
                :class="{'hide-on-small' : !selectedClient && !showSelectClient}"
            >
                <template v-if="!selectedClient">
                    <div v-if="showSelectClient" class="flex align-center gap-4 p-6">
                        <i class="fa-regular fa-chevron-left fa-lg m-r-10 hide-on-regular" @click="removeSelectedClient()"></i>
                        <clients-select-new
                            autoFocus
                            :placeholder="trans('calendar.create_client_label_choose_client')"
                            @clientSelected="setSelectedClient"
                            class="flex-grow"
                        ></clients-select-new>
                    </div>

                    <div class="flex flex-col h-full align-center justify-center p-6">
                        <i class="fa-duotone fa-messages text-primary text-7xl"></i>
                        <p class="text-muted text-lg font-bold text-center"> {{ trans('messages.select_or_start_new') }} </p>
                    </div>
                </template>

                <template v-else>
                    <div class="custom-divider flex align-center p-6">
                        <a :href="`/app/client/${selectedClient.id}`" class="flex align-center gap-4 group">
                            <i class="fa-regular fa-chevron-left fa-lg m-r-10 hide-on-regular" @click="removeSelectedClient()"></i>
                            <Avatar
                                :url="selectedClient.profile_image_url"
                                width="34px"
                                height="34px"
                            ></Avatar>
                            <p class="header-title m-b-0 flex-1 n-color-text group-hover:underline group-active:underline">{{ selectedClient.name }}</p>
                        </a>
                    </div>

                    <ClientMessages
                        text-area-max-height="150px"
                        :is_subscribed="is_subscribed"
                        :initial_client="selectedClient"
                        :messaging_settings="messaging_settings"
                        class="px-6 pb-6 overflow-y-auto flex-1"
                    ></ClientMessages>
                </template>
            </div>
        </div>
</template>

<script>
import ClientMessages from '@/system/components/client/ClientMessages';
import Avatar from '@/system/components/global/Avatar';
import { http } from '@/system/http';
import InfiniteLoading from '@/system/components/global/InfiniteLoading';
import ClientsSelectNew from '@/system/components/global/form/ClientsSelectNew.vue';
import ConversationItem from '@/system/components/global/messages/ConversationItem.vue';

export default{
    data() {
        return {
            messagesLoading: false,
            allConversations: [],
            messagesNextPageUrl: null,
            messagesAllLoaded: false,
            selectedClient: null,
            showSelectClient: false,
        }
    },

    props: {
        messaging_settings: {
            required: true
        },

        is_subscribed: {
            required: true
        },

        initial_client: {
            required: false
        }
    },

    components: {
        ClientMessages,
        Avatar,
        InfiniteLoading,
        ClientsSelectNew,
        ConversationItem,
    },

    async mounted() {
        this.$event_bus.$on('load-messages-overview-conversations', this.loadMessagesOverviewConversations);

        await this.getMessages(true);

        if (this.initial_client) {
            this.setSelectedClient(this.initial_client);
        }
    },

    beforeUnmount() {
        this.$event_bus.$off('load-messages-overview-conversations', this.loadMessagesOverviewConversations);
    },

    methods: {
        getMessages(showLoading) {
            if (this.messagesAllLoaded) return false;

            if (showLoading) this.messagesLoading = true;

            const url = this.messagesNextPageUrl || '/app/messages-overview/getAll';

            return http.get(url)
                .then(data => {
                    this.handleMessagesResponse(data);
                })
                .catch(error => {
                    this.messagesLoading = false;
                    console.error("Error fetching conversations:", error);
                    throw error; //re-throw error so that New Relic will pick it up.
                });
        },

        handleMessagesResponse(data) {
            this.messagesLoading = false;

            this.allConversations = this.allConversations.length === 0 ? data.data.data : [...this.allConversations, ...data.data.data];

            this.messagesNextPageUrl = data.data.links.next;
            this.messagesAllLoaded = !data.data.links.next;
        },

        setSelectedClient(client) {
            if (this.selectedClient && client.id === this.selectedClient.id) {
                return
            }

            this.selectedClient = client;
            this.$event_bus.$emit('clientUpdated', client);

            this.$nextTick(() => {
                this.$event_bus.$emit('load-secure-messages', client.id, true);
            });

            window.history.replaceState(null, null, `?clientId=${client.id}`);

            this.checkAndHandleUnreadMessages();
        },

        checkAndHandleUnreadMessages() {
            this.allConversations.forEach((conversation) => {
                if (this.selectedClient && conversation.client.id === this.selectedClient.id) {
                    conversation.unread_messages_count = 0;
                }
            });
        },

        removeSelectedClient() {
            this.selectedClient = null;
            this.showSelectClient = false;
        },

        previousPage() {
            const referrer = document.referrer;
            const baseUrl = window.Config.get('app.BASEURL');

            if (referrer.startsWith(baseUrl)) {
                window.history.back();
            } else {
                window.location.href = baseUrl + '/calendar';
            }
        },

        loadMessagesOverviewConversations() {
            this.allConversations = [];
            this.messagesNextPageUrl = null;
            this.messagesAllLoaded = false;
            this.getMessages(true);
        },
    },
}
</script>

<style lang="scss">
// TODO: Remove when updated to Tailwind 3
.group {
    &:hover {
        & > .group-hover\:underline {
            text-decoration: underline;
        }
    }
    &:active {
        & > .group-active\:underline {
            text-decoration: underline;
        }
    }
}
</style>
